import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const stats = [
  { value: '50万+', label: '月活跃用户', icon: 'ri-user-line' },
  { value: '5000+', label: '收录游戏', icon: 'ri-gamepad-line' },
  { value: '2000+', label: '原创攻略', icon: 'ri-article-line' },
  { value: '98%', label: '用户好评率', icon: 'ri-thumb-up-line' },
];

const values = [
  {
    title: '专业推荐',
    desc: '每一款推荐游戏都经过编辑团队深度体验和严格评测，确保推荐的品质和可靠性。',
    icon: 'ri-star-line',
  },
  {
    title: '真实评测',
    desc: '我们会认真对待每一位玩家的反馈，提供客观真实的游戏评测，帮助用户做出最佳选择。',
    icon: 'ri-shield-check-line',
  },
  {
    title: '及时更新',
    desc: '团队每日跟踪游戏行业动态，第一时间更新游戏资讯、攻略和新游上线信息。',
    icon: 'ri-flashlight-line',
  },
  {
    title: '用户至上',
    desc: '以用户体验为核心，持续优化平台功能和内容质量，打造最好的h游戏内容平台。',
    icon: 'ri-heart-line',
  },
];

const faqs = [
  {
    q: 'H游戏精选是什么？',
    a: 'H游戏精选是一个专注于h游戏推荐、攻略和评测的中文内容平台。我们致力于为h游戏爱好者提供最新、最全面的游戏资讯和深度内容。',
  },
  {
    q: '如何找到我喜欢的h游戏？',
    a: '你可以通过首页的精选推荐、热门排行榜浏览热门游戏，也可以通过分类页面按游戏类型（如角色扮演、动作冒险、恋爱养成等）筛选，找到最适合你的游戏。',
  },
  {
    q: '平台的游戏推荐是否可靠？',
    a: '我们所有的游戏推荐和评测均由专业编辑团队深度体验后撰写，确保内容的客观性和可靠性。我们还结合了大量玩家反馈进行综合评估。',
  },
  {
    q: '如何获取最新的h游戏资讯？',
    a: '你可以订阅我们的资讯邮件（首页底部），我们将第一时间推送最新的h游戏推荐、攻略和新游上线通知。同时网站也会每日更新内容。',
  },
  {
    q: '我可以投稿或提供游戏建议吗？',
    a: '当然可以！我们非常欢迎玩家投稿和提供建议。请通过下方联系表单与我们取得联系，我们的编辑团队会认真阅读每一条反馈。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('mobile_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }
    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qqupknlsngrm4lm490', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: (formData.get('email') as string || '').trim(),
          name: (formData.get('name') as string || '').trim(),
          message: (formData.get('message') as string || '').trim(),
        }).toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = {}; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢您的来信，我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.toLowerCase().includes('spam') || text.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'H游戏精选',
    url: 'https://xinchengtongxun.cn',
    description: '最全面的h游戏内容导航与评测平台，提供h游戏推荐、攻略、下载指南和排行榜。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hyouxijingxuan.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl leading-relaxed">
              H游戏精选致力于为h游戏爱好者打造最全面的内容导航与评测平台，帮助你快速找到心仪的游戏作品。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-8 relative z-10 mb-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center"
              >
                <span className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${stat.icon} text-lg`}></i>
                </span>
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                  {stat.value}
                </p>
                <p className="text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Brand Story */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  <strong className="text-foreground-900">H游戏精选</strong>创立于2025年，是一群热爱游戏的内容创作者共同打造的h游戏推荐平台。我们深知在浩瀚的游戏海洋中寻找值得投入时间的作品有多困难，因此我们致力于为玩家提供最专业、最真实的h游戏推荐和评测。
                </p>
                <p>
                  经过一年多的努力，我们已经成长为拥有<strong className="text-foreground-900">50万月活跃用户</strong>的领先h游戏内容平台。我们的编辑团队每天都在体验新游戏、撰写攻略、更新排行榜，只为帮助每一位玩家找到属于自己的游戏世界。
                </p>
                <p>
                  从最初的几个人到现在超过20人的专业编辑团队，我们始终坚持一个信念：<strong className="text-foreground-900">让每个玩家都能找到心仪的游戏</strong>。无论是角色扮演、动作冒险还是恋爱养成，我们确保每一篇内容都值得你信赖。
                </p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cozy%20creative%20office%20space%20with%20gaming%20setup%2C%20warm%20ambient%20lighting%2C%20team%20collaboration%20atmosphere%2C%20plants%20and%20wooden%20furniture%2C%20editorial%20photography%20style%20with%20soft%20focus&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="H游戏精选团队"
                title="H游戏精选 - 专业h游戏推荐团队"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-background-100/50 py-14 mb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">
              我们的承诺
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8 max-w-lg mx-auto">
              四大核心价值观，指引我们不断前行
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((val) => (
                <div key={val.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                  <span className="w-11 h-11 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${val.icon} text-lg`}></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">{val.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 mb-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            你可能想了解的信息
          </p>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <details key={faq.q} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <h3 className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</h3>
                  <i className="ri-add-line text-foreground-400 group-open:hidden shrink-0"></i>
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block shrink-0"></i>
                </summary>
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-2xl mx-auto px-4 md:px-6 mb-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            有任何问题或建议？我们很乐意听取你的意见
          </p>
          <form
            onSubmit={handleContactSubmit}
            data-readdy-form=""
            className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-5"
          >
            <input type="text" name="mobile_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="contact_alt_field" />
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                姓名
              </label>
              <input
                type="text"
                id="contact-name"
                name="name"
                required
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="请输入你的姓名"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                邮箱
              </label>
              <input
                type="email"
                id="contact-email"
                name="email"
                required
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="请输入你的邮箱地址"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                placeholder="请输入你想说的话（500字以内）"
              ></textarea>
              <p className="text-[11px] text-foreground-400 mt-1">最多500个字符</p>
            </div>
            <button
              type="submit"
              disabled={formStatus === 'loading'}
              className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
            >
              {formStatus === 'loading' ? '提交中...' : '发送消息'}
            </button>
            {formStatus !== 'idle' && (
              <p className={`text-center text-xs ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                {formMessage}
              </p>
            )}
          </form>
        </section>

        <Footer />
      </div>
    </>
  );
}